import {type ApolloError} from '@apollo/client';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';

const DEFAULT_MESSAGE = 'Failed to load some metrics';

/**
 * Failure notice shared by the metric-search-backed pickers. Without it a
 * failed query is indistinguishable from a project with no matching keys.
 *
 * Takes both failures a picker can have and decides between them here, so the
 * pickers don't each restate the rules:
 *
 * - `searchErrorMessage` is the backend rejecting the typed pattern as a regex
 *   it can't run. It wins, and gets no Retry: rerunning a rejected pattern
 *   returns the same rejection, so the pattern is what has to change.
 * - `error` is a failure a retry could clear, and shows the generic message.
 *
 * Renders nothing when neither is set, so callers can drop it in
 * unconditionally.
 */
export const MetricSearchErrorNotice = ({
  searchErrorMessage,
  error,
  onRetry,
}: {
  searchErrorMessage?: string;
  error?: ApolloError;
  onRetry?: () => void;
}) => {
  if (searchErrorMessage == null && error == null) {
    return null;
  }

  const isRejection = searchErrorMessage != null;

  return (
    <div
      className="flex items-center gap-8 text-red-600 dark:text-red-400"
      data-test="metric-search-error"
      role="alert">
      <Icon name="warning" className="h-16 w-16 shrink-0" />
      <span className="text-sm">{searchErrorMessage ?? DEFAULT_MESSAGE}</span>
      {!isRejection && onRetry != null && (
        <Button
          aria-label="Retry loading metrics"
          onClick={onRetry}
          size="small"
          startIcon="reload-refresh"
          variant="ghost">
          Retry
        </Button>
      )}
    </div>
  );
};
